/*
 * Форматирование дат, размеров и длительностей.
 *
 * Всё через Intl с локалью ru-RU — правила склонения, названия месяцев
 * и порядок частей даты берутся из платформы, а не пишутся вручную.
 */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const timeFormat = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const weekdayFormat = new Intl.DateTimeFormat('ru-RU', { weekday: 'short' });
const dayMonthFormat = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });
const fullDateFormat = new Intl.DateTimeFormat('ru-RU', {
	day: 'numeric',
	month: 'long',
	year: 'numeric'
});

/** Начало суток для указанной отметки времени. */
function startOfDay(ts: number): number {
	const d = new Date(ts);
	d.setHours(0, 0, 0, 0);
	return d.getTime();
}

/** Сколько суток назад: 0 — сегодня, 1 — вчера. */
export function daysAgo(ts: number, now = Date.now()): number {
	return Math.round((startOfDay(now) - startOfDay(ts)) / DAY);
}

/** Время «14:32» — для метки под сообщением. */
export function formatTime(ts: number): string {
	return timeFormat.format(ts);
}

/**
 * Отметка в списке чатов. Сегодняшние сообщения показываются временем,
 * вчерашние словом, на этой неделе — днём недели, дальше — датой.
 * Так строка остаётся короткой и не переносится в узкой панели.
 */
export function formatChatListTime(ts: number, now = Date.now()): string {
	const days = daysAgo(ts, now);

	if (days === 0) return timeFormat.format(ts);
	if (days === 1) return 'вчера';
	if (days < 7) return weekdayFormat.format(ts);

	return new Intl.DateTimeFormat('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit'
	}).format(ts);
}

/** Разделитель дат в ленте сообщений. */
export function formatDateSeparator(ts: number, now = Date.now()): string {
	const days = daysAgo(ts, now);

	if (days === 0) return 'Сегодня';
	if (days === 1) return 'Вчера';

	const sameYear = new Date(ts).getFullYear() === new Date(now).getFullYear();
	return sameYear ? dayMonthFormat.format(ts) : fullDateFormat.format(ts);
}

/**
 * Присутствие: «в сети» либо «был(а) …».
 * Род берётся из пола, если он известен; иначе — нейтральная формулировка,
 * чтобы не угадывать по имени.
 */
export function formatPresence(presence: string, lastSeenAt?: number, now = Date.now()): string {
	if (presence === 'online') return 'в сети';
	if (!lastSeenAt) return 'не в сети';

	const diff = now - lastSeenAt;

	if (diff < MINUTE) return 'был(а) только что';
	if (diff < HOUR) {
		const minutes = Math.floor(diff / MINUTE);
		return `был(а) ${minutes} ${plural(minutes, 'минуту', 'минуты', 'минут')} назад`;
	}
	if (daysAgo(lastSeenAt, now) === 0) {
		return `был(а) сегодня в ${timeFormat.format(lastSeenAt)}`;
	}
	if (daysAgo(lastSeenAt, now) === 1) {
		return `был(а) вчера в ${timeFormat.format(lastSeenAt)}`;
	}

	return `был(а) ${dayMonthFormat.format(lastSeenAt)}`;
}

/**
 * Русское склонение по числу.
 * `plural(2, 'минуту', 'минуты', 'минут')` → 'минуты'
 */
export function plural(n: number, one: string, few: string, many: string): string {
	const mod10 = n % 10;
	const mod100 = n % 100;

	if (mod10 === 1 && mod100 !== 11) return one;
	if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
	return many;
}

/** Длительность звонка: «5:07» или «1:12:45». */
export function formatDuration(seconds: number): string {
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = Math.floor(seconds % 60);

	const pad = (n: number) => String(n).padStart(2, '0');

	return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Длительность словами — для истории звонков: «21 мин», «1 ч 0 мин». */
export function formatCallDuration(seconds: number): string {
	if (seconds < 60) return `${seconds} с`;

	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes} мин`;

	const hours = Math.floor(minutes / 60);
	return `${hours} ч ${minutes % 60} мин`;
}

/** Размер файла: «2,3 МБ». */
export function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} Б`;

	const units = ['КБ', 'МБ', 'ГБ'];
	let value = bytes / 1024;
	let unitIndex = 0;

	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024;
		unitIndex++;
	}

	const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
	return `${String(rounded).replace('.', ',')} ${units[unitIndex]}`;
}

/** Число непрочитанных: свыше 999 показывается как «999+». */
export function formatUnread(count: number): string {
	return count > 999 ? '999+' : String(count);
}
